import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  IconButton,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { spacing, Box } from "@mui/system";
import { Print, FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import useAppSelector from "../../../hooks/useAppSelector";
import useAppDispatch from "../../../hooks/useAppDispatch";

import { fetchAssociation } from "../../../redux/slices/association";
import useAuth from "../../../hooks/useAuth";
import { fetchProperties } from "../../../redux/slices/property";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

const PrintableTable = styled(Table)`
  @media print {
    width: 100% !important;
    & .MuiTableCell-root {
      border: 1px solid #000 !important;
      font-size: 9px !important;
      padding: 3px !important;
    }
    & .MuiTableHead-root .MuiTableCell-root {
      background-color: #f5f5f5 !important;
      font-weight: bold !important;
    }
  }
`;

const PrintableContainer = styled("div")`
  @media print {
    & .no-print {
      display: none !important;
    }
    & .MuiBreadcrumbs-root {
      display: none !important;
    }
    & .MuiDivider-root {
      display: none !important;
    }
  }
`;

// Category labels for display
const categoryLabels: Record<string, string> = {
  ASUNTO: "Asunto",
  V_YMP: "Ympärivuotinen vapaa-ajan",
  V_KESA: "Kesäkäyttöinen vapaa-ajan",
  V_LOMA: "Loma-asunto",
  P_KASVI: "Kasvinviljely",
  P_NAUTA: "Nautakarja",
  M_1: "Metsä 1",
  M_2: "Metsä 2",
  M_3: "Metsä 3",
  M_4: "Metsä 4",
  M_5: "Metsä 5",
  MUU: "Muu",
};

interface PropertyUnitization {
  id: number;
  category: string;
  quantity: number;
  distanceKm: number;
  corrDistance: number;
  corrLateral: number;
  corrGeneral: number;
  weightTons: number;
  tonKm: number;
  notes?: string;
}

interface Property {
  user?: { firstName: string; lastName: string };
  code?: string;
  name?: string;
  propertyUnitizations?: PropertyUnitization[];
}

// Format number with 3 decimal places Finnish locale
const fmt3 = (v: number | null | undefined): string =>
  (v ?? 0).toLocaleString("fi-FI", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

function UnitizationList(props: { association: any; properties: Property[] }) {
  // Group properties by owner, then flatten unitizations
  const groupedData: {
    [ownerName: string]: {
      property: Property;
      unitization: PropertyUnitization;
    }[];
  } = {};

  props.properties?.forEach((property: Property) => {
    if (
      property.user &&
      property.propertyUnitizations &&
      property.propertyUnitizations.length > 0
    ) {
      const ownerName = `${property.user.firstName} ${property.user.lastName}`;
      if (!groupedData[ownerName]) {
        groupedData[ownerName] = [];
      }
      property.propertyUnitizations.forEach((unitization) => {
        groupedData[ownerName].push({ property, unitization });
      });
    }
  });

  // Grand totals
  let grandTotalQuantity = 0;
  let grandTotalWeightTons = 0;
  let grandTotalTonKm = 0;

  const handlePrint = () => {
    window.print();
  };

  const handleExportExcel = () => {
    const excelRows: (string | number)[][] = [];

    // Header info
    excelRows.push([`${props.association.name} - Yksiköintiluettelo`]);
    excelRows.push([]);

    // Table headers
    excelRows.push([
      "Omistaja",
      "Kiinteistö",
      "Kategoria",
      "Määrä",
      "Etäisyys (km)",
      "Korj. et.",
      "Korj. siv.",
      "Korj. yl.",
      "Paino (t)",
      "Tonnikm",
      "Muistiinpanot",
    ]);

    let excelGrandQuantity = 0;
    let excelGrandWeight = 0;
    let excelGrandTonKm = 0;

    Object.entries(groupedData).forEach(([ownerName, items]) => {
      let userTotalQuantity = 0;
      let userTotalWeightTons = 0;
      let userTotalTonKm = 0;

      items.forEach(({ property, unitization }) => {
        excelRows.push([
          ownerName,
          `${property.code || ""} (${property.name || ""})`,
          categoryLabels[unitization.category] || unitization.category,
          unitization.quantity,
          unitization.distanceKm,
          unitization.corrDistance,
          unitization.corrLateral,
          unitization.corrGeneral,
          unitization.weightTons,
          unitization.tonKm,
          unitization.notes || "",
        ]);

        userTotalQuantity += unitization.quantity || 0;
        userTotalWeightTons += unitization.weightTons || 0;
        userTotalTonKm += unitization.tonKm || 0;
      });

      // User summary row
      excelRows.push([
        `Yhteensä - ${ownerName}`,
        "",
        "",
        userTotalQuantity,
        "",
        "",
        "",
        "",
        userTotalWeightTons,
        userTotalTonKm,
        "",
      ]);

      excelGrandQuantity += userTotalQuantity;
      excelGrandWeight += userTotalWeightTons;
      excelGrandTonKm += userTotalTonKm;
    });

    // Grand total row
    excelRows.push([
      "Kaikki yhteensä",
      "",
      "",
      excelGrandQuantity,
      "",
      "",
      "",
      "",
      excelGrandWeight,
      excelGrandTonKm,
      "",
    ]);

    // Create workbook and download
    const ws = XLSX.utils.aoa_to_sheet(excelRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Yksiköinti");
    XLSX.writeFile(wb, `yksikointi_${props.association.name}.xlsx`);
  };

  const hasData = Object.keys(groupedData).length > 0;

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            {props.association.name} - Yksiköintiluettelo
          </Typography>
          <Box className="no-print">
            <IconButton onClick={handleExportExcel} title="Lataa Excel">
              <FileDownload />
            </IconButton>
            <IconButton onClick={handlePrint} title="Tulosta">
              <Print />
            </IconButton>
          </Box>
        </Box>
      </CardContent>

      {!hasData ? (
        <CardContent>
          <Typography color="textSecondary">Ei yksiköintitietoja.</Typography>
        </CardContent>
      ) : (
        <Paper>
          <PrintableTable size="small">
            <TableHead>
              <TableRow>
                <TableCell>Omistaja</TableCell>
                <TableCell>Kiinteistö</TableCell>
                <TableCell>Kategoria</TableCell>
                <TableCell align="right">Määrä</TableCell>
                <TableCell align="right">Etäisyys (km)</TableCell>
                <TableCell align="right">Korj. et.</TableCell>
                <TableCell align="right">Korj. siv.</TableCell>
                <TableCell align="right">Korj. yl.</TableCell>
                <TableCell align="right">Paino (t)</TableCell>
                <TableCell align="right">Tonnikm</TableCell>
                <TableCell>Muistiinpanot</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(groupedData).map(
                ([ownerName, items], ownerIndex) => {
                  let userTotalQuantity = 0;
                  let userTotalWeightTons = 0;
                  let userTotalTonKm = 0;

                  const userRows = items.map(
                    ({ property, unitization }, itemIndex) => {
                      userTotalQuantity += unitization.quantity || 0;
                      userTotalWeightTons += unitization.weightTons || 0;
                      userTotalTonKm += unitization.tonKm || 0;

                      return (
                        <TableRow key={`${ownerIndex}-${itemIndex}`}>
                          <TableCell>{ownerName}</TableCell>
                          <TableCell>
                            {`${property.code || ""} (${property.name || ""})`}
                          </TableCell>
                          <TableCell>
                            {categoryLabels[unitization.category] ||
                              unitization.category}
                          </TableCell>
                          <TableCell align="right">
                            {fmt3(unitization.quantity)}
                          </TableCell>
                          <TableCell align="right">
                            {fmt3(unitization.distanceKm)}
                          </TableCell>
                          <TableCell align="right">
                            {fmt3(unitization.corrDistance)}
                          </TableCell>
                          <TableCell align="right">
                            {fmt3(unitization.corrLateral)}
                          </TableCell>
                          <TableCell align="right">
                            {fmt3(unitization.corrGeneral)}
                          </TableCell>
                          <TableCell align="right">
                            {fmt3(unitization.weightTons)}
                          </TableCell>
                          <TableCell align="right">
                            {fmt3(unitization.tonKm)}
                          </TableCell>
                          <TableCell>{unitization.notes || ""}</TableCell>
                        </TableRow>
                      );
                    }
                  );

                  // Update grand totals
                  grandTotalQuantity += userTotalQuantity;
                  grandTotalWeightTons += userTotalWeightTons;
                  grandTotalTonKm += userTotalTonKm;

                  return (
                    <React.Fragment key={ownerIndex}>
                      {userRows}
                      {/* User Summary Row */}
                      <TableRow
                        style={{
                          fontWeight: "bold",
                          backgroundColor: "#f0f0f0",
                        }}
                      >
                        <TableCell>
                          <strong>Yhteensä - {ownerName}</strong>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right">
                          <strong>{fmt3(userTotalQuantity)}</strong>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right">
                          <strong>{fmt3(userTotalWeightTons)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{fmt3(userTotalTonKm)}</strong>
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                }
              )}

              {/* Grand Summary Row */}
              <TableRow
                style={{ fontWeight: "bold", backgroundColor: "#d0d0d0" }}
              >
                <TableCell>
                  <strong>Kaikki yhteensä</strong>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell align="right">
                  <strong>{fmt3(grandTotalQuantity)}</strong>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell align="right">
                  <strong>{fmt3(grandTotalWeightTons)}</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{fmt3(grandTotalTonKm)}</strong>
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </PrintableTable>
        </Paper>
      )}
    </Card>
  );
}

function Unitization() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { association } = useAppSelector((state) => state.association);
  const { properties } = useAppSelector((state) => state.property);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  useEffect(() => {
    dispatch(fetchAssociation(associationId));
    dispatch(fetchProperties(associationId));
  }, [associationId, dispatch]);

  return (
    <PrintableContainer>
      <Helmet title="Yksiköinti" />
      <Typography
        variant="h3"
        gutterBottom
        display="inline"
        className="no-print"
      >
        Yksiköinti
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2} className="no-print">
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/accounting">
          Talous
        </Link>
        <Typography>Yksiköinti</Typography>
      </Breadcrumbs>

      <Divider my={6} className="no-print" />

      {association && properties && (
        <UnitizationList association={association} properties={properties} />
      )}
    </PrintableContainer>
  );
}

export default Unitization;
